/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Vue} from 'vue-property-decorator'
import UploadField from '@/components/shared/UploadField'
import {AnalysisService} from '@/services/AnalysisService'
import AnalysisServiceMixin from '@/services/AnalysisServiceMixin'
import MessageBox from '@/components/shared/MessageBox'
import Multiselect from 'vue-multiselect'
import {RuleSetVO} from '@/valueObjects/RuleSetVO'
import {OsVersionVO} from '@/valueObjects/OsVersionVO'
import YaraServiceMixin from '@/services/YaraServiceMixin'
import YaraService from '@/services/YaraService'
import OsVersionService from '@/services/OsVersionService'
import OsVersionServiceMixin from '@/services/OsVersionServiceMixin'
import {DEFAULT_OSVERSION, DEFAULT_VISIBILITY, ERROR_MESSAGE_TYPE, VISIBILITY_LIST} from '@/utils/constants'
import {Getter, Mutation} from 'vuex-class'
import {UserVO} from '@/valueObjects/UserVO'

@Component({
    mixins: [AnalysisServiceMixin, YaraServiceMixin, OsVersionServiceMixin],
    components: {
        MessageBox,
        Multiselect,
        UploadField,
    },
})
export default class NewAnalysis extends Vue {
    @Getter('getCurrentUser') private currentUser: UserVO
    @Mutation('setStoreMessage') private setStoreMessage: any

    private message = ''
    private messageType = ERROR_MESSAGE_TYPE

    private analysisService: AnalysisService  // redeclared variable to avoid warnings in Webstorm
    private analysisParameters: { [index: string]: any } = {}
    private file: any = null

    private yaraService: YaraService
    private rulesets: RuleSetVO[] = []
    private selectedRulesets: RuleSetVO[] = []

    private isVirusTotalSelected: boolean = false
    private isPFAVSelected: boolean = false

    private visibility: string = DEFAULT_VISIBILITY
    private visibilityList: string[] = VISIBILITY_LIST

    private isDynamicAnalysisSelected: boolean = false
    private selectedOs = new OsVersionVO(DEFAULT_OSVERSION.description, DEFAULT_OSVERSION.osVersion)
    private osList: OsVersionVO[] = []
    private osVersionService: OsVersionService
    private isInteractiveGuiSelected: boolean = false

    private isInternetSelected: boolean = true

    private timeSelector: number = 60
    private vtHash = ""
    private isVtUrl: boolean = false
    private url = ""
    private isWebUrl: boolean = false

    public async mounted(): Promise<void> {

        try {
            this.message = ''
            this.rulesets = await this.yaraService.getRuleSets()
        } catch (e) {
            this.message = `The loading of Yara rules failed. ${e.errorMessage}`
        }

        try {
            this.osList = await this.osVersionService.getOsVersions()
        } catch (e) {
            this.message = `The loading of available os versions failed. ${e.errorMessage}`
        }

        this.applyUserPreferences()
    }

    public async submit(): Promise<void> {

        //console.log(this.timeSelector)
        //console.log(this.file + " " + this.vtHash)
        this.clearMessage()
        if (this.file === undefined && this.vtHash == '' && this.url == '') {
            this.message = 'File is not defined, can not submit analysis'
            this.messageType = ERROR_MESSAGE_TYPE
            return
        }
        if (this.vtHash!= "") this.isVtUrl = true
        if (this.url!= "") this.isWebUrl = true
        const analysisParameters: {[key: string]: string|boolean|number} = {
            ruleset_subset: this.selectedRulesets.map((ruleSetVO: RuleSetVO) => {
                    return ruleSetVO.name
            }).join(),
            dynamic_analysis_os_version: this.isDynamicAnalysisSelected ? this.selectedOs.osVersion : '',
            visibility: this.visibility,
            is_interactive: this.isInteractiveGuiSelected,
            timeSelector: this.timeSelector,
            is_vt_url : this.isVtUrl,
            is_web_url : this.isWebUrl,
        }
        if (this.isPFAVSelected === undefined){
            this.isPFAVSelected = false
        }
        if (this.isInternetSelected === undefined){
            this.isInternetSelected = true
        }
        if (this.isInternetSelected === false){
            this.isInternetSelected = true
        }
        if (this.isDynamicAnalysisSelected === undefined){
            this.isDynamicAnalysisSelected = false
        }
        if (this.isInternetSelected === true && this.isDynamicAnalysisSelected ===false){
            this.isInternetSelected = false
        }
        try {
            let analysisResult
            if (this.file != undefined){ 
                let num: number = 0
                while(this.file.get(num)!=null){
                analysisResult = await this.analysisService.processAnalysis(this.file.get(num),
                    this.isVirusTotalSelected, this.isPFAVSelected, this.isDynamicAnalysisSelected,this.isInternetSelected, analysisParameters)
                num ++
                }
                if (num==1) this.redirectToAnalysisInProgressPage(analysisResult)
                else this.$router.push({name: 'analysislist'})
            }else if (this.vtHash != "" && this.isVtUrl){
                analysisResult = await this.analysisService.processAnalysis(this.vtHash,
                    this.isVirusTotalSelected, this.isPFAVSelected, this.isDynamicAnalysisSelected,this.isInternetSelected, analysisParameters)
                this.redirectToAnalysisInProgressPage(analysisResult)
            }else if (this.url != "" && this.isWebUrl){
                analysisResult = await this.analysisService.processAnalysis(this.url,
                    this.isVirusTotalSelected, this.isPFAVSelected, this.isDynamicAnalysisSelected,this.isInternetSelected, analysisParameters)
                this.redirectToAnalysisInProgressPage(analysisResult)
            }
        } catch (e) {
            this.messageType = ERROR_MESSAGE_TYPE
            this.message = 'Analysis has not been submitted. ' + e.errorMessage +" "+ e.errorDetail
        }
    }

    private changeFile(file: any) {
        this.file = file
        //console.log(this.file)
    }

    private clearMessage() {
        this.message = ''
    }

    private redirectToAnalysisInProgressPage(analysisResult: {[key: string]: any}) {
        if (analysisResult.hasOwnProperty('id')) {
            const analysisResultId = analysisResult.id.toString()

            // Use of store to pass message to a non-parent component
            this.setStoreMessage('New analysis was successfully posted and will start shortly.')
            this.$router.push({name: 'analysis-summary', params: { analysisId: analysisResultId}})

        } else {
        throw new Error('Error in new analysis processing: unexpected content answer from server')}
    }

    private multiselectTextOnselectedOverflow(count: number): string {
        return `and ${count} more`
    }

    private applyUserPreferences() {
        if (this.visibilityList.indexOf(this.currentUser.visibility) !== -1) {
            this.visibility = this.currentUser.visibility
        }

        if (this.currentUser.osVersion.isIn(this.osList)) {
            this.selectedOs = this.currentUser.osVersion
        }

        this.isVirusTotalSelected = this.currentUser.virusTotalSelected

        this.isPFAVSelected = this.currentUser.pfavSelected

        this.isInteractiveGuiSelected = this.currentUser.isGuiInteractive

        this.isDynamicAnalysisSelected = this.currentUser.DynamicAnalysisSelected

        this.isInternetSelected = this.currentUser.InternetSelected

    }
}
